import { EngagementScoreTerminationReason } from '@quickplay/player-interface';
import {
  EngagementBehaviorAggregate,
  deriveUnmutedActiveRatio,
  deriveVisibilityRatio,
} from './EngagementBehaviorTracker';
import { calculateEngagementScore, createEngagementScoreResult } from './EngagementScore';
import { FinalizedLiveProgramContribution } from './LiveProgramEngagementScoreTracker';

interface LiveProgramSegmentScore {
  readonly coverage: number;
  readonly activeRatio: number;
  readonly watchedDuration: number;
  readonly pauseDuration: number;
  readonly eligibleDuration: number;
  readonly behaviorAggregate: EngagementBehaviorAggregate;
}

interface LiveChannelEngagementScoreTrackerOptions {
  onScore: (score: string) => void;
}

export function createLiveChannelEngagementScoreTracker({ onScore }: LiveChannelEngagementScoreTrackerOptions) {
  let finalizedSegments: LiveProgramSegmentScore[] = [];
  let finalized = false;

  return {
    includeSegment(segmentScore: FinalizedLiveProgramContribution): void {
      if (finalized) {
        return;
      }

      finalizedSegments.push({
        coverage: segmentScore.coverage,
        activeRatio: segmentScore.activeRatio,
        watchedDuration: segmentScore.watchedDuration,
        pauseDuration: segmentScore.pauseDuration,
        eligibleDuration: segmentScore.eligibleDuration,
        behaviorAggregate: segmentScore.behaviorAggregate,
      });
    },

    finalize(terminationReason: EngagementScoreTerminationReason): void {
      if (finalized) {
        return;
      }

      finalized = true;
      if (finalizedSegments.length === 0) {
        console.info(`[Engagement Score] live-channel finalize skipped no segments reason=${terminationReason}`);
        return;
      }

      const watchedDuration = finalizedSegments.reduce((sum, score) => sum + score.watchedDuration, 0);
      const pauseDuration = finalizedSegments.reduce((sum, score) => sum + score.pauseDuration, 0);
      const eligibleDuration = finalizedSegments.reduce((sum, score) => sum + score.eligibleDuration, 0);
      const weightedCoverage = weightedAverage(finalizedSegments, 'coverage', watchedDuration);
      const weightedActiveRatio = weightedAverage(finalizedSegments, 'activeRatio', watchedDuration);

      const aggregatedBehavior = aggregateBehavior(finalizedSegments);
      const unmutedMetrics = deriveUnmutedActiveRatio(aggregatedBehavior);
      const visibilityRatio = deriveVisibilityRatio(aggregatedBehavior);

      const score = calculateEngagementScore({
        coverage: weightedCoverage,
        activeRatio: weightedActiveRatio,
        unmutedActiveRatio: unmutedMetrics.unmutedActiveRatio,
        visibilityRatio,
      });
      if (score === undefined) {
        console.info('[Engagement Score] live-channel finalize skipped score undefined');
        return;
      }

      const engagementScore = createEngagementScoreResult({
        scope: 'live-channel',
        score,
        coverage: weightedCoverage,
        activeRatio: weightedActiveRatio,
        watchedDuration,
        pauseDuration,
        eligibleDuration,
        terminationReason,
        unmutedActiveRatio: unmutedMetrics.unmutedActiveRatio,
        mutedWatchDuration: unmutedMetrics.mutedWatchDuration,
        activePlaybackDuration: unmutedMetrics.activePlaybackDuration,
      });

      onScore(engagementScore);
      console.info(`[Engagement Score] live-channel emitted ${engagementScore}`);
    },

    reset(): void {
      finalizedSegments = [];
      finalized = false;
    },
  };
}

function weightedAverage(
  segments: LiveProgramSegmentScore[],
  component: 'coverage' | 'activeRatio',
  watchedDuration: number,
): number {
  if (watchedDuration <= 0) {
    return 0;
  }

  const weightedSum = segments.reduce((sum, segment) => sum + segment[component] * segment.watchedDuration, 0);
  return weightedSum / watchedDuration;
}

function aggregateBehavior(segments: LiveProgramSegmentScore[]): EngagementBehaviorAggregate {
  return segments.reduce<EngagementBehaviorAggregate>(
    (aggregate, segment) => ({
      activePlaybackDuration: aggregate.activePlaybackDuration + segment.behaviorAggregate.activePlaybackDuration,
      mutedActivePlaybackDuration:
        aggregate.mutedActivePlaybackDuration + segment.behaviorAggregate.mutedActivePlaybackDuration,
      hasUnknownMuteState: aggregate.hasUnknownMuteState || segment.behaviorAggregate.hasUnknownMuteState,
      visibilitySupported: aggregate.visibilitySupported || segment.behaviorAggregate.visibilitySupported,
      visibilityWeightedActiveDuration:
        aggregate.visibilityWeightedActiveDuration + segment.behaviorAggregate.visibilityWeightedActiveDuration,
      visibilityHasValidSample:
        aggregate.visibilityHasValidSample || segment.behaviorAggregate.visibilityHasValidSample,
      visibilityMissingSampleDuringActivePlayback:
        aggregate.visibilityMissingSampleDuringActivePlayback ||
        segment.behaviorAggregate.visibilityMissingSampleDuringActivePlayback,
      visibilityInvalid: aggregate.visibilityInvalid || segment.behaviorAggregate.visibilityInvalid,
      visibilityDetached: aggregate.visibilityDetached || segment.behaviorAggregate.visibilityDetached,
    }),
    {
      activePlaybackDuration: 0,
      mutedActivePlaybackDuration: 0,
      hasUnknownMuteState: false,
      visibilitySupported: false,
      visibilityWeightedActiveDuration: 0,
      visibilityHasValidSample: false,
      visibilityMissingSampleDuringActivePlayback: false,
      visibilityInvalid: false,
      visibilityDetached: false,
    },
  );
}
